import { castArray } from 'lodash';
import { map } from 'rxjs';
import { validate as validateUuid } from 'uuid';
import type {
  OnyxDropdownOptionsInput,
  OnyxOption,
  OnyxOptionsGroup,
} from '../../../directives/interfaces';
import { DropdownHelper } from '../../../helpers/dropdown.helper';
import { UtilityHelper } from '../../../helpers/utility.helper';
import type { OnyxOptional } from '../../../interfaces';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxFilterFieldVariant, OnyxFilterOperator } from '../enums';
import type { OnyxFilter, OnyxFilterField } from '../interfaces';

export class OnyxFilterHelper {
  private static readonly I18N = `${I18N_NAMESPACE}.table.filters.operators`;
  private static readonly EMPTY_VALUE = 'null';
  private static readonly DEFAULT_OPERATORS: Record<
    OnyxFilterFieldVariant,
    OnyxFilterOperator
  > = {
    [OnyxFilterFieldVariant.DROPDOWN_SINGLE]: OnyxFilterOperator.EQ,
    [OnyxFilterFieldVariant.DROPDOWN_MULTI]: OnyxFilterOperator.IN,
    [OnyxFilterFieldVariant.TABS]: OnyxFilterOperator.EQ,
    [OnyxFilterFieldVariant.FREETEXT]: OnyxFilterOperator.INCLUDES,
    [OnyxFilterFieldVariant.NUMBER]: OnyxFilterOperator.RANGE,
    [OnyxFilterFieldVariant.DATE]: OnyxFilterOperator.RANGE,
  };
  private static readonly RANGE_OPERATORS = [
    OnyxFilterOperator.RANGE,
    OnyxFilterOperator.EQ,
    OnyxFilterOperator.LT,
    OnyxFilterOperator.GT,
  ];
  private static readonly VARIANT_OPERATORS: Partial<
    Record<OnyxFilterFieldVariant, OnyxFilterOperator[]>
  > = {
    [OnyxFilterFieldVariant.FREETEXT]: [
      OnyxFilterOperator.INCLUDES,
      OnyxFilterOperator.EXCLUDES,
    ],
    [OnyxFilterFieldVariant.NUMBER]: [
      ...OnyxFilterHelper.RANGE_OPERATORS,
      OnyxFilterOperator.NULL,
    ],
    [OnyxFilterFieldVariant.DATE]: OnyxFilterHelper.RANGE_OPERATORS,
  };

  public static getOperators(field: OnyxFilterField) {
    return (
      field.operators ??
      OnyxFilterHelper.VARIANT_OPERATORS[field.variant] ??
      []
    ).map((value) => ({
      name: `${OnyxFilterHelper.I18N}.${value}`,
      value,
    }));
  }

  public static getDefaultOperator(field: OnyxFilterField): OnyxFilterOperator {
    return (
      field.operators?.at(0) ??
      OnyxFilterHelper.DEFAULT_OPERATORS[field.variant]
    );
  }

  public static create(field: OnyxFilterField): OnyxFilter {
    return OnyxFilterHelper.createEmpty(
      field.key,
      OnyxFilterHelper.getDefaultOperator(field),
    );
  }

  public static changeOperator(
    filter: OnyxFilter,
    operator: OnyxFilterOperator,
  ): OnyxFilter {
    if (filter.operator === operator) return filter;

    if (
      filter.operator !== OnyxFilterOperator.RANGE &&
      filter.operator !== OnyxFilterOperator.IN &&
      filter.operator !== OnyxFilterOperator.NULL &&
      operator !== OnyxFilterOperator.RANGE &&
      operator !== OnyxFilterOperator.IN &&
      operator !== OnyxFilterOperator.NULL
    ) {
      return { ...filter, operator };
    }

    return {
      ...filter,
      ...OnyxFilterHelper.createEmpty(filter.key, operator),
    };
  }

  public static hasValue(filter: OnyxFilter): boolean {
    switch (filter.operator) {
      case OnyxFilterOperator.RANGE:
        return filter.value.from != null || filter.value.to != null;
      case OnyxFilterOperator.IN:
        return !!filter.value.length;
      default:
        return filter.value != null;
    }
  }

  public static isValid(filter: OnyxFilter): boolean {
    if (filter.operator !== OnyxFilterOperator.RANGE) return true;

    const { from, to } = filter.value;
    if (from == null || to == null) return true;

    const [fromNumber, toNumber] = [Number(from), Number(to)];
    return isNaN(fromNumber) || isNaN(toNumber)
      ? String(from) <= String(to)
      : fromNumber <= toNumber;
  }

  public static count(filters: OnyxOptional<OnyxFilter[]>): number {
    return (
      filters?.filter((filter) => OnyxFilterHelper.hasValue(filter)).length ?? 0
    );
  }

  public static getReadonlyFilters(filters: OnyxFilter[]): OnyxFilter[] {
    return filters.filter((filter) => filter.readonly);
  }

  public static getEditableFilters(
    filters: OnyxOptional<OnyxFilter[]>,
  ): OnyxFilter[] {
    return filters?.filter((filter) => !filter.readonly) ?? [];
  }

  public static find(
    filters: OnyxOptional<OnyxFilter[]>,
    key: string,
  ): OnyxFilter | undefined {
    return filters?.find((filter) => filter.key === key);
  }

  public static merge(
    readonlyFilters: OnyxFilter[],
    filters: OnyxOptional<OnyxFilter[]>,
  ): OnyxFilter[] {
    const readonlyFields = new Set(readonlyFilters.map((filter) => filter.key));

    return [
      ...readonlyFilters,
      ...OnyxFilterHelper.getEditableFilters(filters).filter(
        (filter) => !readonlyFields.has(filter.key),
      ),
    ];
  }

  public static getDropdownOptions<OptionValue, Value>(
    field: OnyxFilterField,
    options: OnyxDropdownOptionsInput<OptionValue, Value>,
    emptyValueLabel: string,
    customValues: string[],
  ): OnyxDropdownOptionsInput<OptionValue, Value> {
    const { allowCustomValues } = field;
    const allowEmptyValue =
      field.allowEmptyValue &&
      field.variant === OnyxFilterFieldVariant.DROPDOWN_MULTI;
    if (
      !DropdownHelper.isSource(options) ||
      (!allowCustomValues && !allowEmptyValue)
    ) {
      return options;
    }

    const { idKey } = options;
    const createOption = (name: string, value: string) =>
      ({
        name,
        value: idKey ? { [idKey]: value } : value,
      }) as OnyxOption<OptionValue>;

    const emptyOption = createOption(
      emptyValueLabel,
      OnyxFilterHelper.EMPTY_VALUE,
    );

    const getPinnedGroups = (
      query: string,
    ): OnyxOptionsGroup<OptionValue>[] => {
      const matches = (name: string) =>
        UtilityHelper.toCompareString(name).includes(
          UtilityHelper.toCompareString(query),
        );
      const customOptions = customValues
        .filter(matches)
        .map((value) => createOption(value, value));

      return [
        ...(allowEmptyValue && matches(emptyValueLabel)
          ? [{ id: '0.0.empty', options: [emptyOption] }]
          : []),
        ...(customOptions.length
          ? [{ id: '0.1.custom', options: customOptions }]
          : []),
      ];
    };

    return {
      ...options,
      list: (query, pagination) =>
        UtilityHelper.maybeAsyncToObservable(
          options.list(query, pagination),
        ).pipe(
          map((result) => ({
            ...result,
            options: [
              ...(pagination.page === 1 ? getPinnedGroups(query) : []),
              ...(DropdownHelper.mapToGroups(result.options) ?? []),
            ],
          })),
        ),
      get: (value) => {
        const convertedValue = String(value);
        if (
          allowEmptyValue &&
          convertedValue === OnyxFilterHelper.EMPTY_VALUE
        ) {
          return emptyOption;
        }

        if (allowCustomValues && !validateUuid(convertedValue)) {
          return createOption(convertedValue, convertedValue);
        }

        return options.get(value);
      },
    };
  }

  public static getCustomValues(
    field: OnyxFilterField,
    value: unknown,
  ): string[] {
    if (!field.allowCustomValues || value == null) return [];

    return castArray(value)
      .map(String)
      .filter(
        (item) => item !== OnyxFilterHelper.EMPTY_VALUE && !validateUuid(item),
      );
  }

  private static createEmpty(
    key: string,
    operator: OnyxFilterOperator,
  ): OnyxFilter {
    switch (operator) {
      case OnyxFilterOperator.RANGE:
        return { key, operator, value: { from: null, to: null } };
      case OnyxFilterOperator.IN:
        return { key, operator, value: [] };
      case OnyxFilterOperator.NULL:
        return { key, operator, value: true };
      default:
        return { key, operator, value: null };
    }
  }
}
